import classNames from 'classnames';
import { Controller, FieldPath, FieldValues } from 'react-hook-form';
import { FormFeedback } from './FormFeedback';
import { FormField } from './FormField';
import { FormLabel } from './FormLabel';
import { FormFieldProps } from './interfaces';

type Props<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> = FormFieldProps<TFieldValues, TName>;
export const Toggle = <
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>(
  props: Props<TFieldValues, TName>
) => {
  return (
    <Controller
      name={props.name}
      render={({ field, fieldState: { error } }) => {
        return (
          <FormField
            id={props.name}
            className={props.className}
            feedback={
              <FormFeedback
                error={error?.message}
                errorId={`${props.name}-error`}
                errorCyTag={props.errorCyTag ?? (props.cyTag ? `${props.cyTag}-error` : undefined)}
                description={props.description}
                helperId={`${props.name}-helper`}
                reserveSpace={props.reserveFeedbackSpace}
              />
            }
          >
            <span className="flex items-center gap-4">
              <span className="flex min-w-0 flex-1 flex-col gap-2">
                {props.label && (
                  <FormLabel
                    id={`${props.name}-label`}
                    htmlFor={props.name}
                    label={props.label}
                    tooltip={props.tooltip}
                    required={props.required}
                  />
                )}
              </span>
              <span className="flex shrink-0 items-center">
                <label
                  data-cy={props.cyTag || undefined}
                  className={classNames(
                    'relative inline-flex h-5 w-10 shrink-0 rounded-full',
                    props.disabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'
                  )}
                >
                  <input
                    id={props.name}
                    name={field.name}
                    ref={field.ref}
                    type="checkbox"
                    role="switch"
                    disabled={props.disabled}
                    checked={!!field.value}
                    onChange={field.onChange}
                    onBlur={field.onBlur}
                    aria-label={props.label || 'Toggle'}
                    aria-invalid={!!error || undefined}
                    aria-required={props.required || undefined}
                    aria-describedby={
                      error?.message
                        ? `${props.name}-error`
                        : props.description
                          ? `${props.name}-helper`
                          : undefined
                    }
                    aria-errormessage={error?.message ? `${props.name}-error` : undefined}
                    className="peer sr-only"
                  />
                  <span
                    aria-hidden="true"
                    className={classNames(
                      'absolute inset-0 flex items-center rounded-full border-2 border-transparent',
                      'transition-colors duration-200 ease-in-out',
                      'peer-focus-visible:ring-3 peer-focus-visible:ring-primary-50/12 peer-focus-visible:ring-offset-2',
                      field.value ? 'bg-primary-50' : 'bg-grey-500',
                      error && 'ring-3 ring-error-50/12 ring-offset-2'
                    )}
                  >
                    <span
                      className={classNames(
                        'pointer-events-none block size-4 transform rounded-full bg-gray-1 ring-0',
                        'shadow-sm transition duration-200 ease-in-out',
                        field.value ? 'translate-x-5' : 'translate-x-0'
                      )}
                    />
                  </span>
                </label>
              </span>
            </span>
          </FormField>
        );
      }}
    />
  );
};
